import type { DietTrackerDatabase } from "../persistence/indexed-db/database";
import type { BackupStateRecord, PersistedSnapshotV1 } from "../persistence/records";
import {
  USER_DATA_WRITE_LOCK,
  type CrossTabCoordinator,
} from "../browser/cross-tab-coordinator";
import {
  createBackupEnvelope,
  type BackupEnvelope,
  type CurrentBackupEnvelope,
} from "./backup-document";
import { migrateBackupPayload } from "./backup-migrations";
import { parseBackupJson, validateBackupEnvelope, validateSnapshot } from "./validation";

export class LocalBackupManager {
  constructor(
    private readonly database: DietTrackerDatabase,
    private readonly appVersion: string,
    private readonly coordinator?: CrossTabCoordinator,
  ) {}

  async create(): Promise<CurrentBackupEnvelope> {
    const backupState = await this.ensureBackupState();
    const payload = await this.database.exportSnapshot();
    return createBackupEnvelope(payload, {
      appVersion: this.appVersion,
      deviceId: backupState.deviceId,
      revision: backupState.localRevision,
    });
  }

  async restore(input: string | unknown): Promise<{ restored: BackupEnvelope; safetyBackup: CurrentBackupEnvelope }> {
    return this.restoreWithSafetyBackup(input, async () => undefined);
  }

  async restoreWithSafetyBackup(
    input: string | unknown,
    uploadSafetyBackup: (backup: CurrentBackupEnvelope) => Promise<void>,
  ): Promise<{ restored: BackupEnvelope; safetyBackup: CurrentBackupEnvelope }> {
    const parsed = typeof input === "string" ? parseBackupJson(input) : input;
    const envelope = await validateBackupEnvelope(parsed);
    const migrated = validateSnapshot(migrateBackupPayload(envelope.formatVersion, envelope.payload));
    const work = async (): Promise<{ restored: BackupEnvelope; safetyBackup: CurrentBackupEnvelope }> => {
      const safetyBackup = await this.create();
      await validateBackupEnvelope(safetyBackup);
      await uploadSafetyBackup(safetyBackup);
      const currentState = await this.ensureBackupState();

      // Restoring data must not make this physical device impersonate the source
      // device, and it must schedule a fresh post-restore backup.
      const staged: PersistedSnapshotV1 = {
        ...migrated,
        backupState: [{
          id: "google-drive",
          deviceId: currentState.deviceId,
          localRevision: Math.max(currentState.localRevision, envelope.revision) + 1,
          lastBackupId: currentState.lastBackupId,
          lastBackedUpRevision: currentState.lastBackedUpRevision,
          lastBackupAt: currentState.lastBackupAt,
          pending: true,
        }],
      };
      await this.database.replaceFromSnapshot(staged);
      this.coordinator?.publish("database-restored", { backupId: envelope.backupId });
      return { restored: envelope, safetyBackup };
    };
    return this.coordinator
      ? this.coordinator.exclusive(USER_DATA_WRITE_LOCK, work)
      : work();
  }

  async markUploaded(backup: BackupEnvelope): Promise<void> {
    const current = await this.ensureBackupState();
    await this.database.backupState.put({
      ...current,
      lastBackupId: backup.backupId,
      lastBackedUpRevision: backup.revision,
      lastBackupAt: backup.createdAt,
      lastError: undefined,
      pending: current.localRevision > backup.revision,
    });
  }

  async markUploadFailed(message: string): Promise<void> {
    const current = await this.ensureBackupState();
    await this.database.backupState.put({ ...current, lastError: message, pending: true });
  }

  getStatus(): Promise<BackupStateRecord> {
    return this.ensureBackupState();
  }

  private async ensureBackupState(): Promise<BackupStateRecord> {
    const current = await this.database.backupState.get("google-drive");
    if (current) return current;
    const initial: BackupStateRecord = {
      id: "google-drive",
      deviceId: createDeviceId(),
      localRevision: 0,
      pending: false,
    };
    await this.database.backupState.put(initial);
    return initial;
  }
}

function createDeviceId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
